import { Component, HostBinding, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, chartColors } from '../report-aggregation';
import { textOn } from './summary-boxes.component';
import type { BlockPatch } from '../report-design-edit';

type Priority = 'high' | 'medium' | 'low';

interface StoredAction {
  text: string;
  priority: Priority;
}

const PRIORITY_LABELS: Record<Priority, string> = { high: 'High', medium: 'Medium', low: 'Low' };

/**
 * Five is the cap. A closing slide that lists eight things to do is a list nobody acts on, and the
 * numbers stop reading as a priority order.
 */
const MAX_ACTIONS = 5;

@Component({
  selector: 'app-recommended-actions-block',
  standalone: false,
  template: `
    @if (!actions.length && !editing) {
      <p class="grey4 nav-font12 mb-0">
        No actions yet. Turn on <em>Edit Report</em> to write them.
      </p>
    }

    <ol class="action-list list-unstyled mb-0">
      @for (action of actions; track $index) {
        <li class="action d-flex align-items-start p-3 mb-2">
          <span class="action-number nav-font16 font-weight-bold mr-3"
                [style.background-color]="numberBackground"
                [style.color]="numberColour">{{ $index + 1 }}</span>

          <div class="flex-fill">
            @if (editing) {
              <textarea class="action-input nav-font14 w-100"
                        rows="2"
                        [attr.aria-label]="'Action ' + ($index + 1)"
                        placeholder="What should they do?"
                        [value]="action.text"
                        (input)="setText($index, $any($event.target).value)"></textarea>
            } @else {
              <p class="nav-font14 mb-0">{{ action.text }}</p>
            }
          </div>

          @if (editing) {
            <!-- ngModel rather than [value]: a select's value is set before @for has rendered its
                 options, so the browser drops it and shows the first one. Re-picking the value it
                 was already showing fires no change event, so the real priority never got saved. -->
            <select class="action-priority nav-font12 ml-3"
                    [attr.aria-label]="'Priority for action ' + ($index + 1)"
                    [ngModel]="action.priority"
                    (ngModelChange)="setPriority($index, $event)">
              @for (option of priorities; track option) {
                <option [value]="option">{{ priorityLabels[option] }}</option>
              }
            </select>
            <!-- The list is in whatever order it was put in, so moving a row is the only way to
                 change it -- there is no sort to fall back on. -->
            <button type="button"
                    class="action-move btn btn-sm p-0 ml-2"
                    [disabled]="$index === 0"
                    [attr.aria-label]="'Move action ' + ($index + 1) + ' up'"
                    (click)="moveAction($index, -1)">
              <i class="fas fa-arrow-up" aria-hidden="true"></i>
            </button>
            <button type="button"
                    class="action-move btn btn-sm p-0 ml-1"
                    [disabled]="$index === actions.length - 1"
                    [attr.aria-label]="'Move action ' + ($index + 1) + ' down'"
                    (click)="moveAction($index, 1)">
              <i class="fas fa-arrow-down" aria-hidden="true"></i>
            </button>
            <button type="button"
                    class="action-remove btn btn-sm p-0 ml-2"
                    [attr.aria-label]="'Remove action ' + ($index + 1)"
                    (click)="removeAction($index)">
              <i class="fas fa-close" aria-hidden="true"></i>
            </button>
          } @else {
            <span class="action-priority-badge nav-font12 font-weight-bold ml-3"
                  [style.background-color]="priorityBackground(action.priority)"
                  [style.color]="priorityColour(action.priority)">
              {{ priorityLabels[action.priority] }}
            </span>
          }
        </li>
      }
    </ol>

    @if (editing && actions.length < maxActions) {
      <button type="button" class="add-action btn btn-sm p-0 mt-1 nav-font12" (click)="addAction()">
        <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add an action
      </button>
    }
  `,
  styles: [`
    :host { display: block; }
    .action {
      background: var(--ecr-card, #ffffff);
      border: 1px solid var(--ecr-line, #dbe4e8);
      border-radius: var(--ecr-radius-sm, 6px);
    }
    /* A square rather than a circle: the numbers run to two digits at most and a square reads as a
       step in a sequence where a circle reads as a bullet. */
    .action-number {
      flex: 0 0 auto;
      width: 32px;
      height: 32px;
      border-radius: 6px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .action-priority-badge {
      flex: 0 0 auto;
      align-self: flex-start;
      border-radius: 10px;
      padding: 2px 10px;
      white-space: nowrap;
    }
    .action-input, .action-priority {
      background: transparent;
      border: 1px solid var(--nav-grey1);
      border-radius: 3px;
      color: inherit;
      padding: 2px 4px;
    }
    .action-input:hover, .action-priority:hover { border-color: var(--nav-grey2); }
    .action-input:focus, .action-priority:focus {
      background: var(--nav-true-white);
      border-color: var(--nav-grey3);
      outline: none;
    }
    .action-priority { flex: 0 0 auto; align-self: flex-start; }
    .action-remove, .add-action, .action-move {
      background: none;
      border: none;
      color: var(--nav-grey4);
    }
    .action-remove:hover, .add-action:hover, .action-move:hover { color: var(--nav-secondary); }
    .action-move:disabled { opacity: 0.3; }
    .action-move { align-self: flex-start; }
  `]
})
export class RecommendedActionsBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() alignCount?: number;
  @Input() compact?: boolean;
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  @HostBinding('class.pl-5')
  @HostBinding('class.pr-5')
  get fullWidth(): boolean {
    return this.block?.segmentLocation === 'full';
  }

  readonly maxActions = MAX_ACTIONS;
  readonly priorities: Priority[] = ['high', 'medium', 'low'];
  readonly priorityLabels = PRIORITY_LABELS;

  /**
   * The actions in the order they are stored, which is the order they are drawn and numbered.
   * Deliberately not sorted by priority: the order is the author's argument about what to do first,
   * and a sort would overrule it every time a priority changed.
   */
  actions: StoredAction[] = [];

  numberBackground = '';
  numberColour = '';
  private roles: string[] = [];

  ngOnChanges() {
    if (this.block.reportType !== 'action-list') return;

    this.actions = (this.block.reportExtraData.actions ?? [])
      .slice(0, MAX_ACTIONS)
      .map(action => ({
        text: action.text ?? '',
        priority: this.priorities.includes(action.priority as Priority)
          ? action.priority as Priority
          : 'medium',
      }));

    this.roles = chartColors(this.palette, 5);
    this.numberBackground = this.roles[0];
    this.numberColour = textOn(this.roles[0]);
  }

  /** High takes the palette's own accent; the rest step down through it rather than to grey. */
  priorityBackground(priority: Priority): string {
    return { high: this.roles[2], medium: this.roles[1], low: this.roles[3] }[priority];
  }

  priorityColour(priority: Priority): string {
    return textOn(this.priorityBackground(priority));
  }

  setText(index: number, text: string) {
    this.emit(this.actions.map((action, i) => i === index ? { ...action, text } : action));
  }

  setPriority(index: number, priority: string) {
    if (!this.priorities.includes(priority as Priority)) return;
    this.emit(this.actions.map((action, i) =>
      i === index ? { ...action, priority: priority as Priority } : action));
  }

  addAction() {
    if (this.actions.length >= MAX_ACTIONS) return;
    this.emit([...this.actions, { text: '', priority: 'medium' }]);
  }

  removeAction(index: number) {
    this.emit(this.actions.filter((_, i) => i !== index));
  }

  /** Swaps with its neighbour, which is all reordering a list of at most five needs. */
  moveAction(index: number, direction: -1 | 1) {
    const to = index + direction;
    if (to < 0 || to >= this.actions.length) return;

    const next = [...this.actions];
    [next[index], next[to]] = [next[to], next[index]];
    this.emit(next);
  }

  /**
   * The whole array every time, never one entry: the actions are positional, so patching a single
   * index would leave the stored document sparse and renumber everything after it.
   */
  private emit(actions: StoredAction[]) {
    this.onPatch?.({ actions });
  }
}
